import React from 'react';
import PropTypes from 'prop-types';

/**
 * Card Component (Enhanced for NuVola Website Migration)
 * 
 * A comprehensive card component covering all card patterns from the original design.
 * Supports feature cards, testimonials, explore cards, video cards, and help topic cards.
 */

const Card = ({
  children,
  variant = 'default',
  padding = 'default',
  hover = false,
  shadow = 'default',
  border = true,
  className = '',
  onClick,
  ...props
}) => {
  // Base card styles
  const baseStyles = [
    'bg-white',
    'rounded-lg',
    'transition-all',
    'duration-300'
  ];

  // Variant styles based on original design patterns
  const variantStyles = {
    default: [],
    
    // Feature card pattern (HomePage feature cards)
    feature: [
      'overflow-hidden',
      'h-full',
      'border',
      'border-gray-300',
      'shadow-sm',
      'hover:transform',
      'hover:-translate-y-1',
      'hover:shadow-md'
    ],
    
    // Testimonial pattern (HomePage testimonials)
    testimonial: [
      'border-l-4',
      'border-l-nuvola-blue',
      'h-full'
    ],
    
    // Explore card pattern (HomePage explore section)
    explore: [
      'text-center',
      'flex',
      'flex-col',
      'h-full',
      'border',
      'border-gray-200',
      'shadow-sm'
    ],
    
    // Video card pattern (VideoVault page)
    video: [
      'overflow-hidden',
      'h-full',
      'border',
      'border-gray-300',
      'shadow-sm',
      'hover:shadow-md'
    ],
    
    // Help topic card pattern (Help page)
    'help-topic': [
      'h-full',
      'border',
      'border-gray-300',
      'shadow-sm',
      'hover:shadow-md'
    ],
    
    // Download card pattern
    download: [
      'bg-bg-accent'
    ]
  };

  // Padding styles based on design system and original patterns
  const paddingStyles = {
    none: [],
    small: ['p-4'],
    default: ['p-6'],
    large: ['p-8'],
    // Specific patterns from original design
    testimonial: ['p-6'], // 25px from original
    explore: ['p-5'],     // 20px from original
    feature: ['p-0'],     // No padding for feature cards (content controls padding)
    video: ['p-0'],       // No padding for video cards
    'help-topic': ['p-6'] // Standard padding for help cards
  };

  // Shadow styles
  const shadowStyles = {
    none: [],
    small: ['shadow-sm'],
    default: ['shadow'],
    medium: ['shadow-md'],
    large: ['shadow-lg'],
    xl: ['shadow-xl'],
    // Original design shadows
    feature: ['shadow-sm', 'hover:shadow-md'],
    testimonial: ['shadow-sm'],
    explore: ['shadow-sm']
  };

  // Border styles (some variants override this)
  const borderStyles = border && !['feature', 'testimonial', 'explore', 'video', 'help-topic'].includes(variant) ? 
    ['border', 'border-gray-300'] : [];

  // General hover effects (variants can override)
  const hoverStyles = hover && !['feature'].includes(variant) ? [
    'hover:-translate-y-1',
    'hover:shadow-lg',
    'cursor-pointer'
  ] : [];

  // Interactive styles for clickable cards
  const interactiveStyles = onClick ? [
    'cursor-pointer'
  ] : [];

  // For variant-specific padding override
  const appliedPadding = ['feature', 'video', 'testimonial', 'explore', 'help-topic'].includes(variant) ? 
    variant : padding;

  // For variant-specific shadow override  
  const appliedShadow = ['feature', 'testimonial', 'explore'].includes(variant) ? 
    variant : shadow;

  // Combine all styles
  const cardClasses = [
    ...baseStyles,
    ...variantStyles[variant],
    ...paddingStyles[appliedPadding],
    ...shadowStyles[appliedShadow],
    ...borderStyles,
    ...hoverStyles,
    ...interactiveStyles,
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

// Card subcomponents for better composition
const CardHeader = ({ children, className = '', ...props }) => (
  <div className={`mb-4 ${className}`} {...props}>
    {children}
  </div>
);

const CardTitle = ({ children, level = 'h3', className = '', variant = 'default', ...props }) => {
  const Component = level;
  const baseClasses = 'font-semibold text-gray-800 mb-2';
  
  const sizeClasses = {
    h1: 'text-3xl',
    h2: 'text-2xl', 
    h3: 'text-xl',
    h4: 'text-lg',
    h5: 'text-base',
    h6: 'text-sm'
  };

  // Variant-specific title styles
  const variantClasses = {
    default: '',
    feature: 'text-xl mb-3 font-semibold',      // 1.5rem, 15px margin
    testimonial: 'text-lg font-semibold mb-1',   // Person name styling
    explore: 'text-xl mb-5 font-semibold',       // 1.5rem, 20px margin
    video: 'text-lg font-medium mb-2',
    'help-topic': 'text-lg font-medium mb-2'
  };

  const appliedClasses = variant !== 'default' ? 
    `${variantClasses[variant]} text-gray-800` : 
    `${baseClasses} ${sizeClasses[level]}`;

  return (
    <Component 
      className={`${appliedClasses} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

const CardBody = ({ children, variant = 'default', className = '', ...props }) => {
  // Variant-specific body padding
  const variantClasses = {
    default: 'flex-grow',
    feature: 'p-6',
    testimonial: '',
    explore: '',
    video: 'p-4',
    'help-topic': ''
  };

  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

const CardContent = ({ children, variant = 'default', className = '', ...props }) => {
  // Variant-specific content styling
  const variantClasses = {
    default: 'text-gray-600 leading-relaxed',
    feature: 'text-gray-600 mb-4',          // Color #555, 20px margin
    testimonial: 'text-lg italic text-gray-700 mb-4 leading-relaxed', // Quote styling
    explore: 'text-gray-600 mb-5 flex-grow', // Color #555, 20px margin, flex-grow
    video: 'text-gray-600 mb-2',
    'help-topic': 'text-gray-600'
  };

  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

const CardImage = ({ 
  src, 
  alt, 
  height = 'auto',
  objectFit = 'cover',
  variant = 'default',
  className = '',
  ...props 
}) => {
  const heightClasses = {
    auto: 'h-auto',
    small: 'h-32',
    medium: 'h-48',
    large: 'h-64',
    xl: 'h-80',
    // Variant-specific heights from original design
    feature: 'h-56',      // 220px from original
    explore: 'h-50',      // 200px from original
    video: 'h-48',        // Standard video thumbnail height
    'help-topic': 'h-40'  // Help topic icon height
  };

  const objectFitClasses = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none'
  };

  // Apply variant-specific height if variant is provided
  const appliedHeight = variant !== 'default' ? variant : height;

  return (
    <img
      src={src}
      alt={alt}
      className={`w-full ${heightClasses[appliedHeight]} ${objectFitClasses[objectFit]} ${className}`}
      {...props}
    />
  );
};

const CardFooter = ({ children, className = '', ...props }) => (
  <div className={`mt-4 ${className}`} {...props}>
    {children}
  </div>
);

const CardActions = ({ children, align = 'left', variant = 'default', className = '', ...props }) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between'
  };

  // Variant-specific action styling
  const variantClasses = {
    default: '',
    feature: 'p-6 pt-0',      // Padding for feature card actions
    explore: 'mt-auto',       // Push actions to bottom for explore cards
    video: 'p-4 pt-0',
    'help-topic': ''
  };

  return (
    <div 
      className={`flex items-center gap-2 ${alignClasses[align]} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Testimonial-specific subcomponents
const CardQuote = ({ children, className = '', ...props }) => (
  <blockquote className={`text-lg italic text-gray-700 mb-4 leading-relaxed ${className}`} {...props}>
    {children}
  </blockquote>
);

const CardPerson = ({ children, className = '', ...props }) => (
  <div className={`border-t pt-4 ${className}`} {...props}>
    {children}
  </div>
);

const CardPersonName = ({ children, className = '', ...props }) => (
  <p className={`font-semibold text-gray-800 mb-1 ${className}`} {...props}>
    {children}
  </p>
);

const CardPersonDescription = ({ children, className = '', ...props }) => (
  <p className={`text-sm text-gray-600 ${className}`} {...props}>
    {children}
  </p>
);

// Explore card specific container for image
const CardImageContainer = ({ children, variant = 'default', className = '', ...props }) => {
  const variantClasses = {
    default: '',
    explore: 'mb-5 h-50 flex items-center justify-center overflow-hidden' // 200px height, 20px margin
  };

  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// PropTypes for main Card component
Card.propTypes = {
  /** Card content */
  children: PropTypes.node.isRequired,
  
  /** Card variant for different layouts */
  variant: PropTypes.oneOf([
    'default',
    'feature',
    'testimonial', 
    'explore',
    'video',
    'help-topic',
    'download'
  ]),
  
  /** Padding size */
  padding: PropTypes.oneOf(['none', 'small', 'default', 'large', 'testimonial', 'explore', 'feature', 'video', 'help-topic']),
  
  /** Enable hover lift effect */
  hover: PropTypes.bool,
  
  /** Shadow intensity */
  shadow: PropTypes.oneOf(['none', 'small', 'default', 'medium', 'large', 'xl', 'feature', 'testimonial', 'explore']),
  
  /** Show border */
  border: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Click handler for interactive cards */
  onClick: PropTypes.func
};

// PropTypes for subcomponents
CardTitle.propTypes = {
  children: PropTypes.node.isRequired,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  variant: PropTypes.oneOf(['default', 'feature', 'testimonial', 'explore', 'video', 'help-topic']),
  className: PropTypes.string
};

CardBody.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'feature', 'testimonial', 'explore', 'video', 'help-topic']),
  className: PropTypes.string
};

CardContent.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'feature', 'testimonial', 'explore', 'video', 'help-topic']),
  className: PropTypes.string
};

CardImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  height: PropTypes.oneOf(['auto', 'small', 'medium', 'large', 'xl', 'feature', 'explore', 'video', 'help-topic']),
  objectFit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none']),
  variant: PropTypes.oneOf(['default', 'feature', 'explore', 'video', 'help-topic']),
  className: PropTypes.string
};

CardActions.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right', 'between']),
  variant: PropTypes.oneOf(['default', 'feature', 'explore', 'video', 'help-topic']),
  className: PropTypes.string
};

// Export main component and subcomponents
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Body = CardBody;
Card.Content = CardContent;
Card.Image = CardImage;
Card.Footer = CardFooter;
Card.Actions = CardActions;
Card.Quote = CardQuote;
Card.Person = CardPerson;
Card.PersonName = CardPersonName;
Card.PersonDescription = CardPersonDescription;
Card.ImageContainer = CardImageContainer;

export default Card; 